import { Stack, Typography, useTheme } from "@mui/material";
import { Button, InfoIcon } from "components";
import { useAtomValue } from "jotai";
import React from "react";
import { KbFileDetailResponse } from "shared";
import { isPublished } from "../store";
import { BannerButtonType } from "../types";

export const KbBanner = ({
  isLoading = false,
  snapshot,
  kbFileDetails,
  mode,
  tabValue,
  onBannerButtonClick,
  haveFullPermission,
}: {
  isLoading?: boolean;
  snapshot?: KbFileDetailResponse;
  kbFileDetails: KbFileDetailResponse;
  mode: "edit" | "view";
  tabValue?: number;
  onBannerButtonClick: (type: BannerButtonType) => void;
  haveFullPermission: boolean;
}) => {
  const hasUnsavedVersion = snapshot && !snapshot.is_published;
  const hasPublishedVersions = useAtomValue(isPublished);

  const theme = useTheme();
  if (isLoading) {
    return <></>;
  }
  if (
    mode === "view" &&
    kbFileDetails?.is_live &&
    hasUnsavedVersion &&
    hasPublishedVersions
  ) {
    return (
      <Stack
        direction={"row"}
        sx={{
          p: "8px 16px",
          backgroundColor: theme.palette.primitive.system.green[50],
          borderRadius: "0px",
          justifyContent: "space-between",
        }}
      >
        <Stack
          direction={"row"}
          sx={{
            alignItems: "center",
            gap: "8px",
          }}
        >
          <InfoIcon htmlColor={theme.palette.primitive.system.green[800]} />
          <Typography
            variant="subtitle1"
            color={theme.palette.primitive.system.green[800]}
          >
            You’re viewing the published version. An unpublished draft exists
          </Typography>
        </Stack>
        <Stack direction={"row"} gap={"16px"}>
          {haveFullPermission && (
            <Button
              variant="ghost"
              isLink
              onClick={() => onBannerButtonClick("view_draft")}
            >
              View draft version
            </Button>
          )}
        </Stack>
      </Stack>
    );
  }
  if (mode === "view" && !kbFileDetails?.is_live) {
    return (
      <Stack
        direction={"row"}
        sx={{
          p: "8px 16px",
          backgroundColor: "#FFF4CB",
          borderRadius: "0px",
          justifyContent: "space-between",
        }}
      >
        <Stack
          direction={"row"}
          sx={{
            alignItems: "center",
            gap: "8px",
          }}
        >
          <InfoIcon htmlColor={theme.palette.primitive.system.yellow[800]} />
          <Typography
            variant="subtitle1"
            color={theme.palette.primitive.system.yellow[800]}
          >
            You are viewing an unpublished version.
          </Typography>
        </Stack>
        <Stack direction={"row"} gap={"16px"}>
          {haveFullPermission && (
            <Button
              variant="ghost"
              isLink
              onClick={() => onBannerButtonClick("view_published")}
            >
              Switch to published version
            </Button>
          )}
        </Stack>
      </Stack>
    );
  }
  if (mode === "edit" && hasPublishedVersions) {
    return (
      <Stack
        direction={"row"}
        sx={{
          p: "8px 16px",
          backgroundColor: theme.palette.primitive.system.green[50],
          borderRadius: "0px",
          justifyContent: "space-between",
        }}
      >
        <Stack
          direction={"row"}
          sx={{
            alignItems: "center",
            gap: "8px",
          }}
        >
          <InfoIcon htmlColor={theme.palette.primitive.system.green[800]} />
          <Typography
            variant="subtitle1"
            color={theme.palette.primitive.system.green[800]}
          >
            You are editing a published document.
          </Typography>
        </Stack>
        <Stack direction={"row"} gap={"16px"}>
          {haveFullPermission && (
            <Button
              variant="ghost"
              isLink
              onClick={() => onBannerButtonClick("view_published_new_tab")}
            >
              View published version
            </Button>
          )}
          {haveFullPermission && (
            <Button
              variant="ghost"
              isLink
              onClick={() => onBannerButtonClick("restore_live_version")}
            >
              Discard changes
            </Button>
          )}
        </Stack>
      </Stack>
    );
  }

  return <></>;
};
